import React, { useEffect, useState } from "react";

// Optional hero image (replace with your real asset or swap to another format)
import heroImg from "./assets/surveyqr.avif";

const WHATSAPP_PHONE = "254700000000"; // update (no +)
const whatsappUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
    "Hi! I'm interested in Surveys & Feedback via Dynamic QR."
)}`;

export default function SurveysFeedbackPage() {
    const [navShadow, setNavShadow] = useState(false);

    useEffect(() => {
        const onScroll = () => setNavShadow(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Smooth scroll without changing URL
    const NAV_H = 72;
    function scrollToId(id) {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.pageYOffset - NAV_H - 8;
        window.scrollTo({ top, behavior: "smooth" });
    }
    function onNavClick(id) {
        scrollToId(id);
        const toggle = document.getElementById("svy-nav-toggle");
        if (toggle && toggle.checked) toggle.checked = false;
    }

    return (
        <div className="surveys-page">
            {/* NAV */}
            <header className={`svy-nav ${navShadow ? "scrolled" : ""}`} role="banner">
                <div className="container nav-row">
                    <button
                        type="button"
                        className="brand linklike"
                        onClick={() => onNavClick("home")}
                        aria-label="Go to top"
                    >
                        QR Flex<span className="brand-dot">•</span>Surveys
                    </button>

                    <input id="svy-nav-toggle" type="checkbox" className="nav-toggle" aria-label="Toggle menu" />
                    <label htmlFor="svy-nav-toggle" className="hamburger" aria-hidden="true">
                        <span></span><span></span><span></span>
                    </label>

                    <nav className="menu" aria-label="Main">
                        <button type="button" className="linklike" onClick={() => onNavClick("usecases")}>Use Cases</button>
                        <button type="button" className="linklike" onClick={() => onNavClick("demo")}>Live Demo</button>
                        <button type="button" className="linklike" onClick={() => onNavClick("features")}>Features</button>
                        <button type="button" className="linklike" onClick={() => onNavClick("pricing")}>Pricing</button>
                        <button type="button" className="linklike" onClick={() => onNavClick("faq")}>FAQ</button>
                        <a href={whatsappUrl} className="btn small-cta" target="_blank" rel="noreferrer">WhatsApp</a>
                    </nav>
                </div>
            </header>

            {/* HERO */}
            <section id="home" className="hero">
                <div className="container hero-grid">

                    <div className="hero-copy">
                        <h1>
                            Surveys & Feedback <span className="accent">with Dynamic QR</span>
                        </h1>
                        <p className="lead">
                            Capture insights at the moment of truth—tabletop tents, receipts, posters, packaging, events. Update questions
                            anytime, tag responses with UTM, and export to CSV.
                        </p>

                        <div className="row gap hero-actions">
                            <button type="button" className="btn primary" onClick={() => onNavClick("demo")}>
                                Try the Demo
                            </button>
                            <button type="button" className="btn ghost" onClick={() => onNavClick("features")}>
                                Explore Features
                            </button>
                            <a href={whatsappUrl} className="btn whats" target="_blank" rel="noreferrer">
                                Talk on WhatsApp
                            </a>
                        </div>

                        <ul className="hero-bullets">
                            <li>Swap questions without reprinting</li>
                            <li>Auto-UTM tagging for attribution</li>
                            <li>Geo/device-level scan analytics</li>
                        </ul>
                    </div>

                    
                    {/* Image first on mobile via CSS order */}
                    <div className="hero-art">
                        <img
                            src={heroImg}
                            alt="Customer scanning QR to fill a quick survey"
                            className="hero-img"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section id="usecases" className="section">
                <div className="container">
                    <h2>Where QR Surveys Shine</h2>
                    <p className="muted max">Short, friendly forms that visitors actually complete.</p>

                    <div className="grid usecases">
                        <UseCase emoji="🍽️" title="Restaurants & Cafés">
                            Table tents and receipts: NPS and “How was your meal?” with optional tip/return incentive.
                        </UseCase>
                        <UseCase emoji="🛍️" title="Retail & Pop-Ups">
                            Post-purchase satisfaction; capture emails for warranty/loyalty while feedback is fresh.
                        </UseCase>
                        <UseCase emoji="🎟️" title="Events & Exhibitions">
                            Session ratings, speaker feedback, live polls, and quick sentiment during intermissions.
                        </UseCase>
                        <UseCase emoji="🏥" title="Clinics & Services">
                            After-visit feedback, private experience notes, and follow-up preferences.
                        </UseCase>
                        <UseCase emoji="🏫" title="Education">
                            Workshop surveys, campus services feedback, and anonymous forms.
                        </UseCase>
                        <UseCase emoji="🏢" title="Workplace">
                            Facilities check-ins, pulse checks, and “How did we do?” prompts at helpdesks.
                        </UseCase>
                    </div>
                </div>
            </section>

            {/* DEMO — INLINE SURVEY CARD */}
            <section id="demo" className="section muted-bg">
                <div className="container">
                    <h2>Try a Quick Survey</h2>
                    <p className="muted max">This is a lightweight demo card—real deployments can route to your custom form.</p>

                    <div className="demo-wrap">
                        <SurveyDemoCard />
                        <DemoBenefits />
                    </div>

                    <div className="cta-slab">
                        <p className="muted">Need multilingual forms or WhatsApp follow-ups? Easy.</p>
                        <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Start a Pilot</a>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="features" className="section">
                <div className="container">
                    <h2>Features Built for Feedback</h2>
                    <div className="grid features">
                        <Feature emoji="🔁" title="Dynamic Questions">
                            Edit, reorder, or schedule questions—no reprint needed.
                        </Feature>
                        <Feature emoji="🌍" title="Language Routing">
                            Default to device language; let respondents switch manually.
                        </Feature>
                        <Feature emoji="📈" title="Scan & Response Analytics">
                            View scans/starts/completions by hour, device, and location. Export CSV.
                        </Feature>
                        <Feature emoji="🏷️" title="Auto UTM & Campaign IDs">
                            Attribute responses to channels, stores, or creatives.
                        </Feature>
                        <Feature emoji="🧱" title="Spam & Abuse Controls">
                            Optional cooldowns, per-device limits, and pattern flags.
                        </Feature>
                        <Feature emoji="🔗" title="Integrations">
                            Webhooks & CSV: pipe to Sheets, CRM, or helpdesk.
                        </Feature>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="pricing" className="section">
                <div className="container">
                    <h2>Pricing</h2>
                    <p className="muted max">Simple tiers for pilots to national rollouts.</p>

                    <div className="grid pricing">
                        <div className="price">
                            <h3>Starter</h3>
                            <p className="price-num">KES 3,900 <span>/month</span></p>
                            <ul>
                                <li>3 survey QRs</li>
                                <li>5k scans / month</li>
                                <li>Basic analytics + CSV</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block whats" target="_blank" rel="noreferrer">Choose Starter</a>
                        </div>

                        <div className="price popular" aria-label="Most popular">
                            <div className="badge">Most Popular</div>
                            <h3>Growth</h3>
                            <p className="price-num">KES 9,900 <span>/month</span></p>
                            <ul>
                                <li>10 survey QRs</li>
                                <li>30k scans / month</li>
                                <li>Language routing + Webhooks</li>
                            </ul>
                            <a href={whatsappUrl} className="btn primary block" target="_blank" rel="noreferrer">Choose Growth</a>
                        </div>

                        <div className="price">
                            <h3>Enterprise</h3>
                            <p className="price-num">Custom</p>
                            <ul>
                                <li>Unlimited codes & scans</li>
                                <li>SSO + private API</li>
                                <li>Priority support</li>
                            </ul>
                            <a href={whatsappUrl} className="btn block" target="_blank" rel="noreferrer">Contact Sales</a>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="section">
                <div className="container">
                    <h2>FAQ</h2>
                    <div className="faq">
                        <details>
                            <summary>Can I change survey questions after printing?</summary>
                            <p>Yes. Update instantly or schedule changes—no reprint needed.</p>
                        </details>
                        <details>
                            <summary>Can I collect contact info or route to WhatsApp?</summary>
                            <p>Yes. Add optional contact fields and link to WhatsApp flows after submission.</p>
                        </details>
                        <details>
                            <summary>How do I analyze results?</summary>
                            <p>Use dashboards for scans/starts/completions and export CSV or send via webhook.</p>
                        </details>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="footer">
                <div className="container footer-row">
                    <span className="muted tiny">© {new Date().getFullYear()} QR Flex</span>
                    <button type="button" className="tiny linklike" onClick={() => onNavClick("home")}>
                        Back to top ↑
                    </button>
                </div>
            </footer>
        </div>
    );
}

/* ==== Small building blocks ==== */

function UseCase({ emoji, title, children }) {
    return (
        <div className="usecase">
            <div className="usecase-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}

function Feature({ emoji, title, children }) {
    return (
        <div className="feature">
            <div className="feature-head">
                <span className="emoji" aria-hidden="true">{emoji}</span>
                <h3>{title}</h3>
            </div>
            <p className="muted">{children}</p>
        </div>
    );
}

/** Inline demo survey: rating, quick text, consent (no backend—just UI) */
function SurveyDemoCard() {
    const [rating, setRating] = useState(0);
    const [text, setText] = useState("");
    const [consent, setConsent] = useState(true);
    const [submitted, setSubmitted] = useState(false);

    function submitDemo(e) {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 2500);
    }

    return (
        <form className="svy-card" onSubmit={submitDemo} aria-label="Demo survey form">
            <div className="svy-head">
                <span className="qr-emoji" aria-hidden="true">🔳</span>
                <div>
                    <div className="svy-title">How was your experience today?</div>
                    <div className="svy-sub muted">Tap a rating and leave a quick note (optional).</div>
                </div>
            </div>

            <div className="svy-rating" role="radiogroup" aria-label="Rating">
                {[1, 2, 3, 4, 5].map(n => (
                    <label key={n} className={`star ${rating >= n ? "active" : ""}`}>
                        <input
                            type="radio"
                            name="rating"
                            value={n}
                            onChange={() => setRating(n)}
                            aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        />
                        ★
                    </label>
                ))}
            </div>

            <label className="svy-label" htmlFor="svy-text">Anything to share?</label>
            <textarea
                id="svy-text"
                className="svy-text"
                rows={4}
                placeholder="Loved the service, the cappuccino could be hotter…"
                value={text}
                onChange={(e) => setText(e.target.value)}
            />

            <label className="svy-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                I agree to submit this feedback anonymously.
            </label>

            <div className="row gap wrap">
                <button type="submit" className="btn primary" disabled={submitted || rating === 0}>
                    {submitted ? "Submitted ✓" : "Submit"}
                </button>
                <button type="button" className="btn ghost" onClick={() => { setRating(0); setText(""); setConsent(true); }}>
                    Reset
                </button>
            </div>
        </form>
    );
}

function DemoBenefits() {
    return (
        <div className="svy-benefits">
            <h3>Why QR for surveys?</h3>
            <ul className="list">
                <li><strong>Right moment:</strong> capture while the experience is fresh.</li>
                <li><strong>No apps required:</strong> camera → form → submit.</li>
                <li><strong>Adaptable:</strong> add languages, incentives, or branching logic.</li>
                <li><strong>Attribution:</strong> UTM tags show which poster or store drove responses.</li>
            </ul>
            <div className="row gap wrap">
                <a href={whatsappUrl} className="btn primary" target="_blank" rel="noreferrer">Launch a Pilot</a>
                <button className="btn ghost" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                    Preview on Mobile
                </button>
            </div>
        </div>
    );
}
